// Profile Caching Utilities
// Client-side caching for improved performance

class ProfileCache {
  constructor() {
    this.cache = new Map();
    this.maxSize = 50; // Maximum number of cached profiles
    this.ttl = 5 * 60 * 1000; // 5 minutes TTL
  }

  /**
   * Generate cache key
   */
  getCacheKey(userId, options = {}) {
    const optionsString = JSON.stringify(options, Object.keys(options).sort());
    return `profile:${userId}:${optionsString}`;
  }

  /**
   * Set profile in cache
   */
  set(userId, data, options = {}) {
    const key = this.getCacheKey(userId, options);
    
    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      hits: 0
    });
  }

  /**
   * Get profile from cache
   */
  get(userId, options = {}) {
    const key = this.getCacheKey(userId, options);
    const cached = this.cache.get(key);

    if (!cached) return null;

    // Check if cache is expired
    if (Date.now() - cached.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Update hit count and move to end (LRU)
    cached.hits++;
    this.cache.delete(key);
    this.cache.set(key, cached);

    return cached.data;
  }

  /**
   * Invalidate specific profile cache
   */
  invalidate(userId) {
    // Remove all cache entries for this user
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith(`profile:${userId}:`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear entire cache
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const stats = {
      size: this.cache.size,
      maxSize: this.maxSize,
      profiles: {}
    };

    for (const [key, value] of this.cache.entries()) {
      const userId = key.split(':')[1];
      if (!stats.profiles[userId]) {
        stats.profiles[userId] = {
          entries: 0,
          totalHits: 0,
          oldestEntry: value.timestamp,
          newestEntry: value.timestamp
        };
      }
      
      stats.profiles[userId].entries++;
      stats.profiles[userId].totalHits += value.hits;
      stats.profiles[userId].oldestEntry = Math.min(
        stats.profiles[userId].oldestEntry,
        value.timestamp
      );
      stats.profiles[userId].newestEntry = Math.max(
        stats.profiles[userId].newestEntry,
        value.timestamp
      );
    }

    return stats;
  }

  /**
   * Preload profiles into cache
   */
  async preload(userIds, fetchFunction) {
    const promises = userIds.map(async (userId) => {
      // Skip if already cached
      if (this.get(userId)) return;
      
      try {
        const data = await fetchFunction(userId);
        this.set(userId, data);
      } catch (error) {
        console.error(`Failed to preload profile ${userId}:`, error);
      }
    });

    await Promise.all(promises);
  }

  /**
   * Get cache hit rate
   */
  getHitRate() {
    let totalHits = 0;
    let totalRequests = 0;

    for (const value of this.cache.values()) {
      totalHits += value.hits;
      totalRequests += value.hits + 1; // +1 for the initial cache miss
    }

    return totalRequests > 0 ? (totalHits / totalRequests) * 100 : 0;
  }
}

// Media cache for images and videos
class MediaCache {
  constructor() {
    this.cache = new Map();
    this.maxSize = 100 * 1024 * 1024; // 100MB max cache size
    this.currentSize = 0;
  }

  /**
   * Add media to cache
   */
  async add(url, blob) {
    const size = blob.size;
    
    // Check if adding this would exceed max size
    while (this.currentSize + size > this.maxSize && this.cache.size > 0) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      const removed = this.cache.get(firstKey);
      this.currentSize -= removed.size;
      this.cache.delete(firstKey);
    }

    this.cache.set(url, {
      blob,
      size,
      timestamp: Date.now()
    });
    this.currentSize += size;
  }

  /**
   * Get media from cache
   */
  get(url) {
    const cached = this.cache.get(url);
    if (!cached) return null;

    // Move to end (LRU)
    this.cache.delete(url);
    this.cache.set(url, cached);

    return cached.blob;
  }

  /**
   * Clear media cache
   */
  clear() {
    this.cache.clear();
    this.currentSize = 0;
  }

  /**
   * Get cache size
   */
  getSize() {
    return {
      entries: this.cache.size,
      bytes: this.currentSize,
      megabytes: (this.currentSize / (1024 * 1024)).toFixed(2)
    };
  }
}

// Export singleton instances
export const profileCache = new ProfileCache();
export const mediaCache = new MediaCache();

// Cache warming utilities
export const warmCache = {
  /**
   * Warm profile cache with recently viewed profiles
   */
  async warmRecentProfiles(fetchFunction, limit = 10) {
    try {
      const recentProfiles = JSON.parse(
        localStorage.getItem('recentProfiles') || '[]'
      ).slice(0, limit);
      
      await profileCache.preload(recentProfiles, fetchFunction);
    } catch (error) {
      console.error('Failed to warm profile cache:', error);
    }
  },

  /**
   * Track recently viewed profiles
   */
  trackProfileView(userId) {
    try {
      const recent = JSON.parse(
        localStorage.getItem('recentProfiles') || '[]'
      );
      
      // Remove if already exists and add to front
      const filtered = recent.filter(id => id !== userId);
      filtered.unshift(userId);
      
      // Keep only last 20
      const updated = filtered.slice(0, 20);
      
      localStorage.setItem('recentProfiles', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to track profile view:', error);
    }
  }
};
